import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'

import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdateDepositPromotion from '@/modules/promotion/api/hooks/useMutateUpdateDepositPromotion'
import { CreateDepositPromotionRequest } from '@/modules/promotion/type'
import useQueryGetDepositPromotionById from '@/modules/promotion/api/hooks/useQueryGetDepositPromotionById'
import Spinner from '@/common/components/Spinner'
import DepositPromotionForm from '../../../DepositPromotionForm'
import { DepositPromotionFormValues } from '../../../DepositPromotionForm/components/FormSchema'
import { DepositPromotionQueryKeys } from '@/modules/promotion/keys'
import { ProviderResolutionConflictDialog } from '../../../ProviderResolutionConflictDialog'

export default function EditDepositPromotionForm() {
	const navigate = useNavigate()
	const { id } = useParams()
	const { data: promotion, isLoading } = useQueryGetDepositPromotionById(id!)
	const queryClient = useQueryClient()
	const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
	const [conflictMessage, setConflictMessage] = useState('')

	const { mutate: updateDepositPromotion } = useMutationUpdateDepositPromotion({
		onSuccess: () => {
			toast.success('แก้ไขโปรโมชั่นสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [DepositPromotionQueryKeys.GetDepositPromotionById, id],
			})
			navigate('/operator/deposit-promotion')
		},
		onError: (error) => {
			const axiosError = error as unknown as AxiosError<{
				message: string
				errorCode: string
			}>

			// Check if it's a provider resolution conflict error
			if (
				axiosError.isAxiosError &&
				axiosError.response?.data?.errorCode === 'PROVIDER_RESOLUTION_CONFLICT'
			) {
				setConflictMessage(axiosError.response.data.message)
				setConflictDialogOpen(true)
				return
			}

			// For other errors, show toast
			showErrorToast(error)
		},
	})

	const handleSubmit = (payload: CreateDepositPromotionRequest) => {
		updateDepositPromotion({
			id: id!,
			payload,
		})
	}

	if (isLoading || !promotion) return <Spinner />

	// Transform whitelistGameProviders to whitelistGameProviderIds
	const defaultValues = {
		...promotion,
		whitelistGameProviderIds:
			promotion.whitelistGameProviders?.map((provider) => provider.id) || [],
		presetConfigs: promotion.presetConfigs?.map((config) => ({
			...config,
			turnoverThreshold: config.turnoverThreshold ?? undefined,
			presetId: config.presetId ?? undefined,
			presetNumber: config.presetNumber ?? undefined,
		})),
		poolPresetConfigs: promotion.poolPresetConfigs ?? undefined,
	}

	return (
		<>
			<DepositPromotionForm
				mode="edit"
				defaultValues={
					defaultValues as unknown as Partial<DepositPromotionFormValues>
				}
				onSubmit={handleSubmit}
			/>
			<ProviderResolutionConflictDialog
				isOpen={conflictDialogOpen}
				onClose={() => setConflictDialogOpen(false)}
				message={conflictMessage}
			/>
		</>
	)
}
